import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap'
import Cookies from 'js-cookie'
import toast from 'react-hot-toast'
import ApiEndpoints from './commonComponents/ApiEndpoints'
import { useInstitute } from './commonComponents/InstituteContext'
import { useSidebar } from './SidebarContext'
import { useAuth } from './AuthContext'
import ProfileDropdown from './commonComponents/ProfileDropdown'
import { LuGraduationCap } from 'react-icons/lu'
import { BsEyeFill, BsFunnelFill, BsSearch } from 'react-icons/bs'
import { formatDate } from './admitCardHelpers'

const formatPercentage = (value) => {
  if (value == null || value === '') return '-'
  const raw = String(value).trim()
  if (!raw || raw.toLowerCase() === 'null' || raw.toLowerCase() === 'undefined') {
    return '-'
  }
  return raw.endsWith('%') ? raw : `${raw}%`
}

const StudentCertificates = () => {
  const { studentId } = useParams()
  const navigate = useNavigate()
  const { logo } = useInstitute()
  const { isCollapsed } = useSidebar()
  const { logout } = useAuth()

  const [student, setStudent] = useState(null)
  const [certificates, setCertificates] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [marginLeft, setMarginLeft] = useState('0px')
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
  )
  const [logoError, setLogoError] = useState(false)
  const currentSession =
    Cookies.get('session_name') || Cookies.get('session') || ''
  const [selectedSession, setSelectedSession] = useState('')
  const [showFilterCard, setShowFilterCard] = useState(false)

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        const expandedWidth = 245
        const collapsedWidth = 85
        const gap = 1
        setMarginLeft(
          isCollapsed
            ? `${collapsedWidth + gap}px`
            : `${expandedWidth + gap}px`,
        )
      } else {
        setMarginLeft('0px')
      }
      setIsMobile(window.innerWidth <= 768)
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed])

  useEffect(() => {
    const fetchData = async () => {
      if (!studentId) return
      setLoading(true)
      setError('')
      try {
        const res = await ApiEndpoints.getStudentCertificates(studentId)
        const payload = res?.data || {}
        setStudent(payload.student || null)
        setCertificates(Array.isArray(payload.data) ? payload.data : [])
      } catch (err) {
        setStudent(null)
        setCertificates([])
        const message =
          err?.response?.data?.message ||
          err?.message ||
          'Failed to load certificates.'
        setError(message)
        toast.error(message)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [studentId])

  const sessionOptions = useMemo(() => {
    const fromCertificates = [
      ...new Set(
        (Array.isArray(certificates) ? certificates : [])
          .map((cert) => String(cert?.session || '').trim())
          .filter(Boolean),
      ),
    ]
    const options = [...fromCertificates]
    if (currentSession && !options.includes(currentSession)) {
      options.unshift(currentSession)
    }
    return options
  }, [certificates, currentSession])

  const filteredCertificates = useMemo(() => {
    if (!Array.isArray(certificates) || !certificates.length) return []
    if (!selectedSession) return certificates
    return certificates.filter(
      (cert) =>
        String(cert?.session || '').trim() === String(selectedSession).trim(),
    )
  }, [certificates, selectedSession])

  const handleBack = () => {
    if (studentId) {
      navigate(`/student/profile/${studentId}`)
      return
    }
    navigate(-1)
  }

  const handleViewCertificate = (cert) => {
    const certificateId = cert?.id
    const courseId = cert?.course_id ?? cert?.course?.id
    const classId =
      cert?.class_id ??
      cert?.class?.id ??
      student?.class_id

    if (!studentId || !certificateId) {
      toast.error('Certificate id is missing.')
      return
    }

    if (!courseId || !classId) {
      toast.error('Course or class is missing for this certificate.')
      return
    }

    navigate(`/student/${studentId}/certificates/${certificateId}`, {
      state: {
        studentImage: student?.student_image || null,
        student: student || null,
        course_id: courseId,
        class_id: classId,
      },
    })
  }

  if (loading) {
    return (
      <div
        className="d-flex align-items-center justify-content-center"
        style={{ minHeight: '50vh' }}
      >
        <Spinner animation="border" variant="primary" />
      </div>
    )
  }

  if (error || !student) {
    return (
      <div style={{ maxWidth: '960px', margin: '40px auto', padding: '0 16px' }}>
        <div
          style={{
            border: '1px solid #fecaca',
            borderRadius: '12px',
            background: '#fef2f2',
            padding: '18px',
          }}
        >
          <h5 style={{ margin: 0, color: '#991b1b' }}>Unable to load certificates</h5>
          <p style={{ margin: '8px 0 0', color: '#7f1d1d' }}>
            {error || 'Student data not found.'}
          </p>
        </div>
        <Button className="mt-3" variant="secondary" onClick={handleBack}>
          Back
        </Button>
      </div>
    )
  }

  return (
    <>
      <style>
        {`
          .cert-page {
            width: 100%;
            max-width: 100%;
            margin: 24px 0;
            padding: 0 20px 24px;
            box-sizing: border-box;
          }
          .cert-main-wrap {
            transition: margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1);
            position: relative;
            z-index: 1;
            padding-left: 12px;
            padding-right: 12px;
          }
          .cert-top-header {
            margin-top: -12px;
            border-bottom: 1px solid #d1d3d4;
            background: #f3f4f6;
            padding: 12px 28px;
            width: 100%;
          }
          .cert-top-header-row {
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 12px;
          }
          .cert-title-wrap {
            display: flex;
            align-items: center;
            gap: 12px;
            min-width: 0;
          }
          .cert-header-logo {
            width: 40px;
            height: 40px;
            border-radius: 50%;
            overflow: hidden;
            background: #223843;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
          }
          .cert-list-table-wrap {
            border: 1px solid #D1D3D4;
            border-radius: 12px;
            overflow: hidden;
            background: #fff;
          }
          .cert-list-table {
            width: 100%;
            border-collapse: collapse;
            margin: 0;
          }
          .cert-list-table thead th {
            background: #f3f4f6;
            color: #374151;
            font-size: 13px;
            font-weight: 600;
            padding: 14px 16px;
            border-bottom: 1px solid #D1D3D4;
            font-family: Inter, system-ui, sans-serif;
            white-space: nowrap;
          }
          .cert-list-table tbody td {
            font-size: 14px;
            padding: 14px 16px;
            border-bottom: 1px solid #F3F4F6;
            color: #111827;
            font-family: Inter, system-ui, sans-serif;
            vertical-align: middle;
          }
          .cert-list-table tbody tr:last-child td {
            border-bottom: none;
          }
          .cert-list-mobile-card {
            border: 1px solid #D1D3D4;
            border-radius: 12px;
            background: #fff;
            margin-bottom: 12px;
            overflow: hidden;
          }
          .cert-list-mobile-row {
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 12px;
            padding: 12px 16px;
            border-bottom: 1px solid #F3F4F6;
          }
          .cert-list-mobile-row:last-child {
            border-bottom: none;
          }
          .cert-list-mobile-label {
            font-size: 13px;
            font-weight: 600;
            color: #6b7280;
            font-family: Inter, system-ui, sans-serif;
          }
          .cert-list-mobile-value {
            font-size: 14px;
            color: #111827;
            text-align: right;
            font-family: Inter, system-ui, sans-serif;
          }
          .cert-view-btn {
            display: inline-flex !important;
            align-items: center;
            gap: 6px;
            border-color: #223843 !important;
            color: #223843 !important;
            font-weight: 600;
            background-color: #ffffff !important;
            transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;
          }
          .cert-view-btn:hover,
          .cert-view-btn:focus,
          .cert-view-btn:active {
            background-color: #223843 !important;
            border-color: #223843 !important;
            color: #ffffff !important;
          }
          .cert-view-btn:hover svg,
          .cert-view-btn:focus svg,
          .cert-view-btn:active svg {
            color: #ffffff !important;
            fill: currentColor;
          }
          .cert-toolbar {
            display: flex;
            justify-content: space-between;
            align-items: center;
            flex-wrap: wrap;
            margin-bottom: 16px;
            gap: 8px 12px;
          }
          .cert-filter-btn {
            width: 48px;
            height: 48px;
            border-radius: 50%;
            background-color: #ffffff;
            border: 1px solid #D1D3D4;
            color: #223843;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: all 0.2s ease;
            flex-shrink: 0;
            box-shadow: none;
          }
          .cert-filter-btn:hover,
          .cert-filter-btn:focus-visible {
            background-color: #223843 !important;
            color: #ffffff !important;
            border-color: #223843 !important;
            transform: scale(1.05);
            box-shadow: 0 4px 12px rgba(34, 56, 67, 0.28);
            outline: none;
          }
          .cert-filter-btn.is-active {
            background-color: #223843;
            color: #ffffff;
            border-color: #223843;
          }
          .cert-filter-btn.is-active:hover,
          .cert-filter-btn.is-active:focus-visible {
            background-color: #1f323a !important;
            border-color: #1f323a !important;
            transform: scale(1.05);
            box-shadow: 0 4px 12px rgba(34, 56, 67, 0.35);
          }
          @media (max-width: 768px) {
            .cert-filter-btn {
              width: 44px;
              height: 44px;
            }
          }
        `}
      </style>

      <div className="cert-main-wrap" style={{ marginLeft }}>
        <div className="container-fluid p-0">
          <div style={{ marginLeft: isMobile ? '0' : '-40px' }}>
            <div className="cert-top-header">
              <div className="cert-top-header-row">
                <div className="cert-title-wrap">
                  <div className="cert-header-logo">
                    {!logoError ? (
                      <img
                        src={logo}
                        alt="School Logo"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={() => setLogoError(true)}
                      />
                    ) : (
                      <LuGraduationCap size={20} style={{ color: '#ffffff' }} />
                    )}
                  </div>
                  <h3
                    className="m-0 fw-bold text-start"
                    style={{
                      letterSpacing: '-0.02em',
                      fontSize: 'clamp(18px, 2.5vw, 22px)',
                      color: '#223843',
                      lineHeight: '1.2',
                      fontFamily: 'Inter, system-ui, sans-serif',
                    }}
                  >
                    Certificates
                  </h3>
                </div>
                <div
                  className="d-flex align-items-center gap-3"
                  style={{ justifyContent: 'flex-end' }}
                >
                  {!isMobile && <ProfileDropdown logout={logout} />}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="cert-page">
          <div className="cert-toolbar">
            <button
              type="button"
              className={`cert-filter-btn${showFilterCard ? ' is-active' : ''}`}
              onClick={() => setShowFilterCard((prev) => !prev)}
              title={showFilterCard ? 'Hide Filters' : 'Show Filters'}
              aria-label={showFilterCard ? 'Hide Filters' : 'Show Filters'}
              aria-pressed={showFilterCard}
            >
              <BsFunnelFill size={isMobile ? 18 : 20} />
            </button>
          </div>

          <div
            className="filter-card-container"
            style={{
              maxHeight: showFilterCard ? '1000px' : '0',
              overflow: showFilterCard ? 'visible' : 'hidden',
              transition: 'max-height 0.3s ease-in-out',
              marginBottom: showFilterCard ? (isMobile ? '16px' : '24px') : '0',
            }}
          >
            {showFilterCard && (
              <Card
                style={{
                  border: '1px solid #D1D3D4',
                  borderRadius: '12px',
                  backgroundColor: '#ffffff',
                  overflow: 'visible',
                }}
              >
                <div
                  style={{
                    background: '#ffffff',
                    padding: isMobile ? '16px' : '20px',
                    borderBottom: '1px solid #F3F4F6',
                  }}
                >
                  <div className="d-flex align-items-center gap-3">
                    <div
                      style={{
                        width: isMobile ? '36px' : '40px',
                        height: isMobile ? '36px' : '40px',
                        borderRadius: '50%',
                        backgroundColor: '#223843',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <BsSearch
                        style={{
                          color: '#ffffff',
                          fontSize: isMobile ? '16px' : '18px',
                        }}
                      />
                    </div>
                    <div>
                      <h5
                        className="m-0 fw-semibold text-start"
                        style={{
                          color: '#111827',
                          fontSize: isMobile ? '15px' : '16px',
                          fontFamily: 'Inter, system-ui, sans-serif',
                        }}
                      >
                        Filter Certificates
                      </h5>
                      <p
                        className="m-0 mt-1"
                        style={{ fontSize: '13px', color: '#6b7280' }}
                      >
                        Filter certificates by academic session
                      </p>
                    </div>
                  </div>
                </div>

                <Card.Body
                  className={isMobile ? 'p-3' : 'p-4'}
                  style={{ backgroundColor: '#ffffff' }}
                >
                  <Row className="g-4 text-start">
                    <Col xs={12} md={12} lg={12}>
                      <div
                        style={{
                          padding: isMobile ? '16px' : '20px',
                          backgroundColor: '#F3F4F6',
                          borderRadius: '8px',
                          border: '1px solid #D1D3D4',
                          width: '100%',
                        }}
                      >
                        <div className="d-flex align-items-center gap-2 mb-3">
                          <div
                            style={{
                              width: '32px',
                              height: '32px',
                              borderRadius: '50%',
                              backgroundColor: '#10B981',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}
                          >
                            <BsSearch
                              style={{ color: '#ffffff', fontSize: '16px' }}
                            />
                          </div>
                          <label
                            className="fw-semibold mb-0"
                            style={{
                              color: '#111827',
                              fontSize: '14px',
                              fontFamily: 'Inter, system-ui, sans-serif',
                            }}
                          >
                            Filter by Session
                          </label>
                        </div>
                        <div style={{ position: 'relative', width: '100%' }}>
                          <label
                            className="fw-semibold mb-2 d-block"
                            style={{
                              color: '#6b7280',
                              fontSize: '13px',
                              fontFamily: 'Inter, system-ui, sans-serif',
                            }}
                          >
                            Select Session
                          </label>
                          <Form.Select
                            value={selectedSession}
                            onChange={(e) => setSelectedSession(e.target.value)}
                            style={{
                              borderRadius: '8px',
                              border: '1px solid #D1D3D4',
                              fontSize: '14px',
                              height: isMobile ? '44px' : '40px',
                              width: '100%',
                              maxWidth: '100%',
                              transition: 'all 0.2s ease',
                              backgroundColor: '#ffffff',
                              fontFamily: 'Inter, system-ui, sans-serif',
                            }}
                            onFocus={(e) => {
                              e.target.style.borderColor = '#223843'
                            }}
                            onBlur={(e) => {
                              e.target.style.borderColor = '#D1D3D4'
                            }}
                          >
                            <option value="">All Sessions</option>
                            {sessionOptions.map((session) => (
                              <option key={session} value={session}>
                                {session}
                              </option>
                            ))}
                          </Form.Select>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            )}
          </div>

          {filteredCertificates.length === 0 ? (
            <div
              style={{
                textAlign: 'center',
                padding: '48px 16px',
                color: '#6b7280',
                border: '1px solid #D1D3D4',
                borderRadius: '12px',
                background: '#fff',
                fontFamily: 'Inter, system-ui, sans-serif',
              }}
            >
              No certificates found for this student
              {selectedSession ? ` in session ${selectedSession}` : ''}.
            </div>
          ) : isMobile ? (
            filteredCertificates.map((cert, index) => (
              <div key={cert.id ?? index} className="cert-list-mobile-card">
                <div className="cert-list-mobile-row">
                  <span className="cert-list-mobile-label">S/No</span>
                  <span className="cert-list-mobile-value">{index + 1}</span>
                </div>
                <div className="cert-list-mobile-row">
                  <span className="cert-list-mobile-label">Certificate No.</span>
                  <span className="cert-list-mobile-value">
                    {cert.certificate_number || '—'}
                  </span>
                </div>
                <div className="cert-list-mobile-row">
                  <span className="cert-list-mobile-label">Course</span>
                  <span className="cert-list-mobile-value">
                    {cert.course?.course_name || student.course_name || '—'}
                  </span>
                </div>
                <div className="cert-list-mobile-row">
                  <span className="cert-list-mobile-label">Session</span>
                  <span className="cert-list-mobile-value">{cert.session || '—'}</span>
                </div>
                <div className="cert-list-mobile-row">
                  <span className="cert-list-mobile-label">Issue Date</span>
                  <span className="cert-list-mobile-value">
                    {formatDate(cert.issue_date) || '—'}
                  </span>
                </div>
                <div className="cert-list-mobile-row">
                  <span className="cert-list-mobile-label">Grade</span>
                  <span className="cert-list-mobile-value">{cert.grade || '—'}</span>
                </div>
                <div className="cert-list-mobile-row">
                  <span className="cert-list-mobile-label">Percentage</span>
                  <span className="cert-list-mobile-value">
                    {formatPercentage(cert.percentage)}
                  </span>
                </div>
                <div className="cert-list-mobile-row">
                  <span className="cert-list-mobile-label">Action</span>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    className="cert-view-btn"
                    title="View Certificate"
                    onClick={() => handleViewCertificate(cert)}
                  >
                    <BsEyeFill size={14} />
                  </Button>
                </div>
              </div>
            ))
          ) : (
            <div className="cert-list-table-wrap" style={{ overflowX: 'auto' }}>
              <table className="cert-list-table">
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left' }}>S/No</th>
                    <th style={{ textAlign: 'left' }}>Certificate No.</th>
                    <th style={{ textAlign: 'left' }}>Course</th>
                    <th style={{ textAlign: 'left' }}>Issue Date</th>
                    <th style={{ textAlign: 'center' }}>Grade</th>
                    <th style={{ textAlign: 'center' }}>Percentage</th>
                    <th style={{ textAlign: 'center' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCertificates.map((cert, index) => (
                    <tr key={cert.id ?? index}>
                      <td>{index + 1}</td>
                      <td>{cert.certificate_number || '—'}</td>
                      <td>{cert.course?.course_name || student.course_name || '—'}</td>
                      <td>{formatDate(cert.issue_date) || '—'}</td>
                      <td style={{ textAlign: 'center' }}>{cert.grade || '—'}</td>
                      <td style={{ textAlign: 'center' }}>
                        {formatPercentage(cert.percentage)}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          className="cert-view-btn"
                          title="View Certificate"
                          onClick={() => handleViewCertificate(cert)}
                        >
                          <BsEyeFill size={14} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default StudentCertificates
